import React from 'react';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Switch,
  SwitchProps,
} from '@mui/material';
import {
  Control,
  Controller,
  get,
  RegisterOptions,
} from 'react-hook-form';

type CustomSliderProps = SwitchProps & {
  helperText?: string;
  errors?: Record<string, unknown>;
  control?: Control<any>;
  rules?: Exclude<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs'
  >;
  defaultChecked?: boolean;
  name: string;
  label: string;
  onChange?: (checked: boolean) => Promise<boolean | undefined>;
};

const SimpleSwitch: React.FC<{
  label: string;
  checked: boolean;
  defaultChecked?: boolean;
  error: boolean;
  helperText?: string;
  externalOnChange?: (checked: boolean) => Promise<boolean | undefined>;
  rest: Partial<SwitchProps>;
}> = ({
  label,
  checked,
  defaultChecked,
  error,
  helperText,
  externalOnChange,
  rest,
}) => (
  <FormControl error={error} component="fieldset">
    <FormControlLabel
      label={label}
      control={
        <Switch
          onChange={(_, checked) => {
            if (externalOnChange) externalOnChange(checked);
          }}
          checked={checked ?? defaultChecked ?? false}
          {...rest}
        />
      }
    />
    {(error || helperText) && (
      <FormHelperText error>{helperText}</FormHelperText>
    )}
  </FormControl>
);

const ControlledSwitch: React.FC<{
  name: string;
  label: string;
  control: Control<any>;
  defaultChecked?: boolean;
  rules?: Exclude<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs'
  >;
  error: unknown;
  helperText?: string;
  externalOnChange?: (checked: boolean) => Promise<boolean | undefined>;
  rest: Partial<SwitchProps>;
}> = ({
  name,
  label,
  control,
  defaultChecked,
  rules,
  error,
  helperText,
  externalOnChange,
  rest,
}) => (
  <Controller
    name={name}
    defaultValue={defaultChecked}
    control={control}
    rules={rules}
    render={({ field: { onChange, value, ...controlProps } }) => (
      <FormControl error={Boolean(error)} component="fieldset">
        <FormControlLabel
          label={label}
          control={
            <Switch
              onChange={(_, checked) => {
                if (externalOnChange) externalOnChange(checked);
                onChange(checked);
              }}
              checked={Boolean(value)}
              {...controlProps}
              {...rest}
            />
          }
        />
        {(error || helperText) && (
          <FormHelperText error>
            {(error as { message?: string })?.message || helperText}
          </FormHelperText>
        )}
      </FormControl>
    )}
  />
);

export const BooleanSliderInput: React.FC<CustomSliderProps> = ({
  control,
  name,
  label,
  rules,
  errors,
  helperText,
  defaultChecked,
  onChange: externalOnChange,
  ...rest
}) => {
  const error = get(errors, name);

  // If no control is provided, render as a simple controlled component
  if (!control) {
    return (
      <SimpleSwitch
        label={label}
        checked={rest.checked ?? false}
        defaultChecked={defaultChecked}
        error={Boolean(error)}
        helperText={error?.message || helperText}
        externalOnChange={externalOnChange}
        rest={rest}
      />
    );
  }

  // Otherwise, use react-hook-form Controller
  return (
    <ControlledSwitch
      name={name}
      label={label}
      control={control}
      defaultChecked={defaultChecked}
      rules={rules}
      error={error}
      helperText={helperText}
      externalOnChange={externalOnChange}
      rest={rest}
    />
  );
};
